import {useMemo} from 'react';

import {Box, Typography} from '@mui/material';
import {MuiOtpInput} from 'mui-one-time-password-input';
import {useTranslation} from 'react-i18next';

import {AppButton, AppButtonVariant} from '@/app/shared-components';
import {InviterAvatar} from '@/app/shared-components/InviterAvatar/InviterAvatar';
import {isNumericCharacter} from '@/utils/isNumericCharacter';

import {FdicIcon} from './FdicIcon';

const OTP_LENGTH = 6;

interface SplitsOtpCardProps {
  formattedPhone: string;
  otp: string;
  setOtp: (value: string) => void;
  canResend: boolean;
  resendSeconds: number;
  onResendOtp: () => void;
  onSubmitOtp: (otpValue?: string) => void;
  onGetAvatarImage?: () => React.ReactNode;
  inviterName?: string;
  isGroupJoinFlow?: boolean;
}

export function SplitsOtpCard({
  formattedPhone,
  otp,
  setOtp,
  canResend,
  resendSeconds,
  onResendOtp,
  onSubmitOtp,
  onGetAvatarImage,
  inviterName,
  isGroupJoinFlow = false,
}: SplitsOtpCardProps) {
  const {t} = useTranslation();
  const isSubmitDisabled = useMemo(
    () => otp.length !== OTP_LENGTH,
    [otp.length],
  );

  const buttonLabel = useMemo(() => {
    if (isGroupJoinFlow) {
      return t('groups.enter_otp.button');
    }
    return t('split_bills_page.landing.see_split_details');
  }, [isGroupJoinFlow, t]);

  return (
    <Box className="splits-landing-card splits-landing-card-otp">
      <Box
        component="div"
        className="splits-landing-card-fdic">
        <FdicIcon />
      </Box>
      <Box className="splits-landing-card-otp-body">
        {onGetAvatarImage && onGetAvatarImage()}
        {inviterName && <InviterAvatar inviterName={inviterName} />}
        <Typography
          className="splits-landing-headline splits-landing-otp-title"
          component="h4">
          {t('split_bills_page.landing.otp_title_prefix')}
          <span className="splits-landing-otp-phone">{formattedPhone}</span>
          {t('split_bills_page.landing.otp_title_suffix')}
        </Typography>
        <MuiOtpInput
          id="splits-landing-otp-input"
          className="MuiOtpInput-Box"
          length={OTP_LENGTH}
          value={otp}
          onChange={setOtp}
          onComplete={onSubmitOtp}
          validateChar={isNumericCharacter}
          autoFocus
          TextFieldsProps={{
            sx: {
              '& .MuiOutlinedInput-root': {
                borderRadius: '10px',
              },
            },
            variant: 'outlined',
            style: {
              width: '100%',
              textAlign: 'center',
              maxWidth: '45px',
            },
            inputProps: {
              inputMode: 'numeric',
              pattern: '[0-9]*',
              type: 'text',
              style: {
                textAlign: 'center',
                fontSize: '16px',
                height: '69px',
                padding: 0,
              },
            },
          }}
          style={{
            gap: 4,
            justifyContent: 'center',
          }}
        />
        <Box className="splits-landing-otp-resend">
          {canResend ? (
            <AppButton
              id="splits-landing-otp-resend"
              onClick={onResendOtp}
              label={t('split_bills_page.landing.otp_resend')}
              variant={AppButtonVariant.onText}
            />
          ) : (
            <Typography
              component="span"
              className="splits-landing-otp-resend-countdown">
              {t('split_bills_page.landing.otp_resend_in')} {resendSeconds}
            </Typography>
          )}
        </Box>
        <AppButton
          id="splits-landing-otp-submit"
          onClick={onSubmitOtp}
          label={buttonLabel}
          variant={AppButtonVariant.onPrimary}
          disabled={isSubmitDisabled}
        />
      </Box>
    </Box>
  );
}
